import { msg } from 'gt-react';
import type {
  CorrectionField,
  MemoryProposalKind,
  MemoryProposalStatus,
  MemoryRuleKind,
  MemoryRuleUsageEffect,
} from '@erp/shared-types';

/**
 * Display labels for harness enums, registered with `msg()` so they translate.
 * Decode at the call site with `useMessages()` (`m(LABEL[value])`), never with
 * `gt()` — registration functions only accept static strings.
 */
/** Where a correction was made and where a learned rule applies. */
export { MEMORY_MODULE_LABEL } from '@/components/harness/MemoryModuleSelect';

export const RULE_KIND_LABEL: Record<MemoryRuleKind, string> = {
  customer_alias: msg('Customer alias'),
  location_is_own_site: msg('Our own site'),
  ship_to_alias: msg('Ship-to alias'),
  carrier_term_alias: msg('Carrier term'),
  carrier_account_rule: msg('Carrier account'),
  extraction_hint: msg('Extraction hint'),
  sender_route: msg('Sender route'),
};

export const PROPOSAL_KIND_LABEL: Record<MemoryProposalKind, string> = {
  ...RULE_KIND_LABEL,
  note: msg('Memory note'),
  skill_draft: msg('Skill draft'),
};

export const PROPOSAL_STATUS_LABEL: Record<MemoryProposalStatus, string> = {
  proposed: msg('Proposed'),
  accepted: msg('Accepted'),
  rejected: msg('Rejected'),
  superseded: msg('Superseded'),
  stale: msg('Stale'),
};

export const USAGE_EFFECT_LABEL: Record<MemoryRuleUsageEffect, string> = {
  bound: msg('Bound'),
  suggested: msg('Suggested'),
  excluded: msg('Excluded'),
  hint_injected: msg('Hint injected'),
  mismatch: msg('Mismatch raised'),
};

export const CORRECTION_FIELD_LABEL: Record<CorrectionField, string> = {
  customerId: msg('Customer'),
  shipToLocationId: msg('Ship-to location'),
  billToLocationId: msg('Bill-to location'),
  selectedProductVariantId: msg('Product'),
  shippingMethod: msg('Shipping method'),
  freightTerms: msg('Freight terms'),
  freightCarrierInstructions: msg('Freight carrier instructions'),
  shippingAccountNumber: msg('Shipping account number'),
  orderType: msg('Order type'),
  warehouse: msg('Warehouse'),
  currency: msg('Currency'),
  purchaseOrderNumber: msg('Customer PO #'),
  requestDate: msg('Request date'),
  orderDate: msg('Order date'),
  quantity: msg('Quantity'),
  unitPrice: msg('Unit price'),
  other: msg('Other'),
};

export function statusBadgeVariant(
  status: MemoryProposalStatus,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'proposed':
      return 'default';
    case 'accepted':
      return 'secondary';
    case 'rejected':
      return 'destructive';
    default:
      return 'outline';
  }
}

/** Render a scalar correction value; ids are replaced by labels when known. */
export function displayValue(
  value: unknown,
  labels: Record<string, string>,
): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string') return labels[value] ?? value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  if (typeof value === 'object' && value !== null && 'name' in value) {
    const named = value as { name?: unknown };
    if (typeof named.name === 'string') return named.name;
  }
  return JSON.stringify(value);
}
